import { apiFetch } from "./client.ts";

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

interface PageParams {
  search?: string;
  limit?: number;
  offset?: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function pageQs(params: any): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params as Record<string, unknown>)) {
    if (v != null && v !== "") qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

// ---------------------------------------------------------------------------
// Company
// ---------------------------------------------------------------------------

export interface Company {
  id: string;
  name: string;
  domain: string | null;
  industry: string | null;
  size: string | null;
  website: string | null;
  linkedinUrl: string | null;
  notes: string | null;
  createdBy: string;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export async function listCompanies(token: string, params: PageParams & { archived?: boolean } = {}): Promise<Page<Company>> {
  return apiFetch<Page<Company>>(`/api/v1/companies${pageQs(params)}`, { token });
}

export async function getCompany(token: string, id: string): Promise<Company> {
  return apiFetch<Company>(`/api/v1/companies/${id}`, { token });
}

export async function createCompany(token: string, body: Partial<Company>): Promise<Company> {
  return apiFetch<Company>("/api/v1/companies", { token, method: "POST", body: JSON.stringify(body) });
}

// ---------------------------------------------------------------------------
// Contact
// ---------------------------------------------------------------------------

export interface Contact {
  id: string;
  companyId: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  title: string | null;
  linkedinUrl: string | null;
  notes: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export async function listContacts(token: string, params: PageParams & { companyId?: string; archived?: boolean } = {}): Promise<Page<Contact>> {
  return apiFetch<Page<Contact>>(`/api/v1/contacts${pageQs(params)}`, { token });
}

export async function getContact(token: string, id: string): Promise<Contact> {
  return apiFetch<Contact>(`/api/v1/contacts/${id}`, { token });
}

// ---------------------------------------------------------------------------
// Lead
// ---------------------------------------------------------------------------

export interface Lead {
  id: string;
  companyId: string | null;
  contactId: string | null;
  title: string;
  description: string | null;
  source: string;
  status: string;
  priority: string;
  assignedTo: string | null;
  estimatedValue: number | null;
  currency: string;
  tags: string[];
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface LeadNote {
  id: string;
  leadId: string;
  body: string;
  createdBy: string;
  createdAt: string;
}

export async function listLeads(token: string, params: PageParams & { status?: string; assignedTo?: string; archived?: boolean } = {}): Promise<Page<Lead>> {
  return apiFetch<Page<Lead>>(`/api/v1/leads${pageQs(params)}`, { token });
}

export async function getLead(token: string, id: string): Promise<Lead> {
  return apiFetch<Lead>(`/api/v1/leads/${id}`, { token });
}

export async function getLeadNotes(token: string, leadId: string): Promise<LeadNote[]> {
  return apiFetch<LeadNote[]>(`/api/v1/leads/${leadId}/notes`, { token });
}

export async function addLeadNote(token: string, leadId: string, body: string): Promise<LeadNote> {
  return apiFetch<LeadNote>(`/api/v1/leads/${leadId}/notes`, { token, method: "POST", body: JSON.stringify({ body }) });
}

export async function transitionLead(token: string, id: string, status: string, version: number): Promise<Lead> {
  return apiFetch<Lead>(`/api/v1/leads/${id}/transition`, { token, method: "POST", body: JSON.stringify({ status, version }) });
}

// ---------------------------------------------------------------------------
// Proposal
// ---------------------------------------------------------------------------

export interface Proposal {
  id: string;
  leadId: string | null;
  clientId: string | null;
  title: string;
  description: string | null;
  status: string;
  proposalVersion: number;
  content: string | null;
  estimatedValue: number | null;
  currency: string;
  validUntil: string | null;
  approvalId: string | null;
  workflowRunId: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export async function listProposals(token: string, params: PageParams & { status?: string; leadId?: string; clientId?: string } = {}): Promise<Page<Proposal>> {
  return apiFetch<Page<Proposal>>(`/api/v1/proposals${pageQs(params)}`, { token });
}

export async function getProposal(token: string, id: string): Promise<Proposal> {
  return apiFetch<Proposal>(`/api/v1/proposals/${id}`, { token });
}

export async function generateProposal(token: string, id: string, version: number): Promise<{ data: Proposal; meta: { message: string; workflowId: string } }> {
  return apiFetch<{ data: Proposal; meta: { message: string; workflowId: string } }>(`/api/v1/proposals/${id}/generate`, {
    token, method: "POST", body: JSON.stringify({ version }),
  });
}

export async function submitProposal(token: string, id: string, version: number): Promise<Proposal> {
  return apiFetch<Proposal>(`/api/v1/proposals/${id}/submit`, { token, method: "POST", body: JSON.stringify({ version }) });
}

export async function approveProposal(token: string, id: string, version: number): Promise<Proposal> {
  return apiFetch<Proposal>(`/api/v1/proposals/${id}/approve`, { token, method: "POST", body: JSON.stringify({ version }) });
}

export async function rejectProposal(token: string, id: string, version: number, reason: string): Promise<Proposal> {
  return apiFetch<Proposal>(`/api/v1/proposals/${id}/reject`, { token, method: "POST", body: JSON.stringify({ version, reason }) });
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export interface Client {
  id: string;
  companyId: string | null;
  proposalId: string | null;
  name: string;
  status: string;
  onboardedAt: string | null;
  contractValue: number | null;
  currency: string;
  contractStart: string | null;
  contractEnd: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export async function listClients(token: string, params: PageParams & { status?: string } = {}): Promise<Page<Client>> {
  return apiFetch<Page<Client>>(`/api/v1/clients${pageQs(params)}`, { token });
}

export async function getClient(token: string, id: string): Promise<Client> {
  return apiFetch<Client>(`/api/v1/clients/${id}`, { token });
}

// ---------------------------------------------------------------------------
// Project
// ---------------------------------------------------------------------------

export interface Project {
  id: string;
  clientId: string;
  name: string;
  description: string | null;
  status: string;
  startDate: string | null;
  endDate: string | null;
  budget: number | null;
  currency: string;
  repositoryUrl: string | null;
  notes: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export async function listProjects(token: string, params: PageParams & { clientId?: string; status?: string } = {}): Promise<Page<Project>> {
  return apiFetch<Page<Project>>(`/api/v1/projects${pageQs(params)}`, { token });
}

export async function getProject(token: string, id: string): Promise<Project> {
  return apiFetch<Project>(`/api/v1/projects/${id}`, { token });
}
